/**
 * Centralized Error Handling Service
 * Provides consistent error handling, logging, and recovery across the application
 */

import { logger } from '@/utils/logger';
import { toast } from 'sonner';

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  NETWORK = 'network',
  DATABASE = 'database',
  AUTHENTICATION = 'auth',
  VALIDATION = 'validation',
  PERMISSION = 'permission',
  CONTENT = 'content',
  AUDIO = 'audio',
  GENERATION = 'generation'
}

interface ErrorContext {
  category: ErrorCategory;
  severity: ErrorSeverity;
  userId?: string;
  moduleId?: string;
  courseId?: string;
  metadata?: Record<string, any>;
}

class ErrorHandlingService {
  private errorLog: Array<{ error: Error; context: ErrorContext; timestamp: Date }> = [];
  private readonly MAX_LOG_SIZE = 100;

  /**
   * Handle and log errors with context
   */
  handleError(error: Error, context: ErrorContext): void {
    // Log to error log
    this.errorLog.push({
      error,
      context,
      timestamp: new Date()
    });

    // Trim log if too large
    if (this.errorLog.length > this.MAX_LOG_SIZE) {
      this.errorLog = this.errorLog.slice(-this.MAX_LOG_SIZE);
    }

    // Log to console
    logger.error(`[${context.category}] ${error.message}`, {
      severity: context.severity,
      ...context.metadata
    });

    // Show user-friendly toast for critical errors
    if (context.severity === ErrorSeverity.CRITICAL || context.severity === ErrorSeverity.HIGH) {
      this.showErrorToast(error, context);
    }

    // Send to monitoring service if critical
    if (context.severity === ErrorSeverity.CRITICAL) {
      this.reportToMonitoring(error, context);
    }
  }

  /**
   * Show user-friendly error toast
   */
  private showErrorToast(error: Error, context: ErrorContext): void {
    const userMessage = this.getUserFriendlyMessage(error, context);
    
    toast.error(userMessage.title, {
      description: userMessage.description,
      duration: 5000,
    });
  }

  /**
   * Convert technical errors to user-friendly messages
   */
  private getUserFriendlyMessage(error: Error, context: ErrorContext): { title: string; description: string } {
    switch (context.category) {
      case ErrorCategory.NETWORK:
        return {
          title: 'Connection Issue',
          description: 'Please check your internet connection and try again.'
        };
      case ErrorCategory.AUTHENTICATION:
        return {
          title: 'Authentication Error',
          description: 'Please log in again to continue.'
        };
      case ErrorCategory.PERMISSION:
        return {
          title: 'Access Denied',
          description: 'You don\'t have permission to perform this action.'
        };
      case ErrorCategory.AUDIO:
        return {
          title: 'Audio Playback Error',
          description: 'Unable to play audio. Please check your browser settings.'
        };
      case ErrorCategory.CONTENT:
        return {
          title: 'Content Error',
          description: 'There was an issue loading the content. Please try again.'
        };
      default:
        return {
          title: 'Something went wrong',
          description: 'An unexpected error occurred. Please try again.'
        };
    }
  }

  /**
   * Report critical errors to monitoring
   */
  private reportToMonitoring(error: Error, context: ErrorContext): void {
    // Could integrate with services like Sentry, LogRocket, etc.
    console.error('CRITICAL ERROR:', {
      message: error.message,
      stack: error.stack,
      context
    });
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(count: number = 10): Array<{ error: Error; context: ErrorContext; timestamp: Date }> {
    return this.errorLog.slice(-count);
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Wrap async operations with error handling
   */
  async withErrorHandling<T>(
    operation: () => Promise<T>,
    context: ErrorContext,
    fallback?: T
  ): Promise<T | undefined> {
    try {
      return await operation();
    } catch (error) {
      this.handleError(error as Error, context);
      return fallback;
    }
  }

  /**
   * Retry operation with exponential backoff
   */
  async retryWithBackoff<T>(
    operation: () => Promise<T>,
    context: ErrorContext,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;
        
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt - 1);
          logger.warn(`Retry ${attempt}/${maxRetries} after ${delay}ms`, { error: lastError.message });
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    this.handleError(lastError!, context);
    throw lastError;
  }
}

export const errorHandlingService = new ErrorHandlingService();
export default errorHandlingService;